/**
 * PushTemplateConfig - Template-based push notification configuration
 *
 * Allows selecting a notification template, mapping its variables
 * to upstream sources, previewing the result, and configuring delivery.
 */

import React, { useMemo } from 'react';
import { FieldSourceToggle } from '../FieldSourceToggle';
import { PushDeliveryConfig } from './PushDeliveryConfig';
import { PushNotificationPreview } from './PushNotificationPreview';
import { useI18n } from '@/i18n';
import { useNotificationTemplates } from '@/features/notification-templates/hooks/useNotificationTemplates';
import type { BaseCustomRendererProps } from './types';
import { configFieldKey } from './types';

export const PushTemplateConfig: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
  fieldSourceHandlersFactory,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
}) => {
  const { t } = useI18n();
  const { data: templates = [] } = useNotificationTemplates();

  const handleUpdate = (field: string, value: unknown) => {
    if (onBatchUpdate) {
      onBatchUpdate({ [field]: value });
    }
  };

  const selectedTemplateName = (nodeData.push_template_name as string) || '';
  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.name === selectedTemplateName),
    [templates, selectedTemplateName]
  );

  // Determine which template variables have been mapped
  const mappedVariableNames = useMemo(() => {
    const mapped = new Set<string>();
    if (!selectedTemplate) return mapped;

    for (const variable of selectedTemplate.variables) {
      const fieldKey = configFieldKey(`tpl_${variable.name}`);
      const handlers = fieldSourceHandlersFactory?.createFieldHandlers(fieldKey);
      if (handlers?.mapping) {
        mapped.add(variable.name);
      }
    }
    return mapped;
  }, [selectedTemplate, fieldSourceHandlersFactory]);

  return (
    <div className="space-y-4">
      {/* Template Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('imageWorkflows.pushTarget.selectTemplate')}
        </label>
        <select
          value={selectedTemplateName}
          onChange={(e) => handleUpdate('push_template_name', e.target.value || undefined)}
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
        >
          <option value="">{t('imageWorkflows.pushTarget.selectTemplatePlaceholder')}</option>
          {templates
            .filter((tpl) => tpl.is_active && tpl.channels.includes('push'))
            .map((tpl) => (
              <option key={tpl.name} value={tpl.name}>
                {tpl.display_name}
              </option>
            ))}
        </select>
      </div>

      {selectedTemplate ? (
        <>
          {/* Variable Mappings */}
          {selectedTemplate.variables.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('imageWorkflows.pushTarget.variableMappings')}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {t('imageWorkflows.pushTarget.variableMappingHint')}
              </p>
              <div className="space-y-3">
                {selectedTemplate.variables.map((variable) => {
                  const fieldKey = configFieldKey(`tpl_${variable.name}`);
                  const handlers = fieldSourceHandlersFactory?.createFieldHandlers(fieldKey);

                  return (
                    <FieldSourceToggle
                      key={variable.name}
                      fieldKey={fieldKey}
                      label={variable.label || variable.name}
                      description={variable.description}
                      required={variable.required}
                      availableSources={availableSources}
                      customInputs={customInputs}
                      contextVariables={contextVariables}
                      startVariables={startVariables}
                      mapping={handlers?.mapping}
                      onMappingChange={handlers?.onMappingChange || (() => {})}
                      onKeyPathChange={handlers?.onKeyPathChange || (() => {})}
                      onMappingRemove={handlers?.onMappingRemove || (() => {})}
                      disabled={isDisabled}
                      compatibleTypes={[variable.type === 'number' ? 'number' : 'string']}
                    >
                      <span className="text-xs text-gray-400 italic">
                        {handlers?.mapping
                          ? undefined
                          : variable.default || t('imageWorkflows.pushTarget.notMapped')}
                      </span>
                    </FieldSourceToggle>
                  );
                })}
              </div>
            </div>
          )}

          {/* Preview */}
          <PushNotificationPreview
            template={selectedTemplate}
            mappedVariableNames={mappedVariableNames}
          />
        </>
      ) : (
        <p className="text-sm text-gray-400 italic text-center py-4">
          {t('imageWorkflows.pushTarget.templateNotSelected')}
        </p>
      )}

      <hr className="border-gray-200" />

      {/* Delivery Config (target, sound, badge) */}
      <PushDeliveryConfig
        nodeData={nodeData}
        isDisabled={isDisabled}
        onUpdate={handleUpdate}
      />
    </div>
  );
};

export default PushTemplateConfig;
